import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, Download, Lock, Plus, Receipt, Tag } from 'lucide-react';
import {
  monthOf,
  PAYMENT_METHODS,
  parseRupees,
  type Expense,
  type ExpenseCategory,
  type PaymentMethod,
  type PnlBasis,
  type RecurrenceFrequency,
} from '@tailoring-crm/shared';
import { RecordExpenseDialog } from '../components/expenses/record-expense-dialog.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { LoadError } from '../components/feedback/load-error.js';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Badge,
  Button,
  useConfirm,
  Input,
  Label,
  ListSkeleton,
  useToast,
  cn,
} from '@tailoring-crm/ui';
import { API_BASE_URL } from '../lib/api/base-url.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { expensesCsvPath } from '../lib/expenses/expenses-api.js';
import {
  useConfirmPendingExpense,
  useCreateExpenseCategory,
  useCreateRecurringExpense,
  useDeleteExpense,
  useDeleteRecurringExpense,
  useDismissPendingExpense,
  useExpenseCategories,
  useExpenses,
  usePendingExpenses,
  useRecurringExpenses,
  useUpdateExpenseCategory,
} from '../lib/expenses/use-expenses.js';
import { formatCurrency, formatDate } from '../lib/format.js';

/**
 * What the shop spends, on one screen with three tabs.
 *
 * The BOOK is the default and is what an owner opens: a month of expenses, the month's total at
 * the top, and one button to add another. It is also the drill-down target of every expense
 * figure on the P&L — the `month`, `basis` and `categoryId` in the URL are the same parameters
 * the statement computed with, so arriving here from a tapped number shows exactly the rows
 * behind it and the same total.
 *
 * REPEATING is the standing instructions and, above them, the queue of rows they have generated
 * and nobody has confirmed. The queue sits first because it is the only part of this screen with
 * something waiting on the owner.
 *
 * CATEGORIES is the shop's own list. Renaming and archiving; no delete, because last March's
 * P&L still has to say "Rent".
 *
 * A karigar sees none of it. The API refuses every endpoint, so the screen says so plainly
 * rather than rendering a page of failed requests.
 */

type Tab = 'book' | 'repeating' | 'categories';

export function ExpensesPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [tab, setTab] = useState<Tab>('book');

  if (role === 'tailor') {
    return (
      <>
        <PageHeader title={t('expenses.title')} />
        <EmptyState
          icon={Lock}
          title={t('expenses.noAccessTitle')}
          description={t('expenses.noAccessHint')}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('expenses.title')} description={t('expenses.description')} />

      <div className="flex flex-col gap-4">
        <div
          role="tablist"
          aria-label={t('expenses.title')}
          className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1"
        >
          {(['book', 'repeating', 'categories'] as const).map((one) => (
            <button
              key={one}
              type="button"
              role="tab"
              aria-selected={tab === one}
              onClick={() => setTab(one)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                tab === one
                  ? 'border-transparent bg-primary text-primary-foreground'
                  : 'border-border hover:bg-accent',
              )}
            >
              {t(`expenses.tab.${one}`)}
            </button>
          ))}
        </div>

        {tab === 'book' ? <ExpenseBook /> : null}
        {tab === 'repeating' ? <RepeatingPanel /> : null}
        {tab === 'categories' ? <CategoriesPanel /> : null}
      </div>
    </>
  );
}

// --- The book ----------------------------------------------------------------------------

function ExpenseBook(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();

  const month = params.get('month') ?? monthOf(new Date());
  const basis = (params.get('basis') === 'cash' ? 'cash' : 'accrual') as PnlBasis;
  const categoryId = params.get('categoryId') ?? '';

  const categories = useExpenseCategories();
  const list = useExpenses({
    month,
    basis,
    ...(categoryId ? { categoryId } : {}),
  });
  const remove = useDeleteExpense();

  const [dialog, setDialog] = useState<{ expense: Expense | null } | null>(null);

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const data = list.data;
  const categoryList = categories.data?.items ?? [];
  const activeCategory = categoryList.find((one) => one.id === categoryId);

  const del = async (expense: Expense): Promise<void> => {
    const ok = await confirm({
      title: t('expenses.deleteTitle'),
      description: t('expenses.deleteHint', { amount: formatCurrency(expense.amountPaise) }),
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(expense.id);
      toast({ title: t('expenses.deleted'), variant: 'success' });
    } catch (caught) {
      toast({
        title: caught instanceof Error ? caught.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="expenses-month">{t('expenses.month')}</Label>
          <Input
            id="expenses-month"
            type="month"
            value={month}
            className="max-w-44"
            onChange={(event) => setParam('month', event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="expenses-category">{t('expenses.category')}</Label>
          <select
            id="expenses-category"
            value={categoryId}
            onChange={(event) => setParam('categoryId', event.target.value)}
            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
          >
            <option value="">{t('expenses.allCategories')}</option>
            {categoryList.map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </select>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button asChild variant="outline">
            {/* A plain link: the API answers with the file, and the session cookie travels
                with the request exactly as it does for every other call. */}
            <a
              href={`${API_BASE_URL}${expensesCsvPath({
                month,
                basis,
                ...(categoryId ? { categoryId } : {}),
              })}`}
            >
              <Download aria-hidden="true" />
              {t('expenses.export')}
            </a>
          </Button>
          <Button type="button" onClick={() => setDialog({ expense: null })}>
            <Plus aria-hidden="true" />
            {t('expenses.record')}
          </Button>
        </div>
      </div>

      {/* Which date the month was measured by. It arrives in the URL from the P&L, so the two
          screens can never disagree about what "August" contained. */}
      <p className="text-sm text-muted-foreground" role="status">
        {basis === 'cash' ? t('expenses.basisCashNote') : t('expenses.basisAccrualNote')}
        {activeCategory ? ` · ${activeCategory.label}` : ''}
      </p>

      {list.isPending ? (
        <ListSkeleton rows={4} />
      ) : list.isError ? (
        <LoadError onRetry={() => void list.refetch()} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title={t('expenses.emptyTitle')}
          description={t('expenses.emptyHint')}
        />
      ) : (
        <>
          <p className="rounded-lg border border-border p-3 text-sm" role="status">
            {t('expenses.monthTotal', {
              amount: formatCurrency(data.totalPaise),
              count: data.total,
            })}
          </p>

          <ul className="flex flex-col gap-2">
            {data.items.map((expense) => (
              <li
                key={expense.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {expense.categoryLabel}
                    {expense.systemGenerated ? (
                      <Badge variant="secondary">{t('expenses.systemBadge')}</Badge>
                    ) : null}
                    {expense.paidOn === null ? (
                      <Badge variant="warning">{t('expenses.unpaidBadge')}</Badge>
                    ) : null}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(expense.incurredOn)}
                    {expense.vendor ? ` · ${expense.vendor}` : ''}
                    {` · ${t(`payments.methods.${expense.method}`)}`}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <p className="font-semibold tabular-nums">
                    {formatCurrency(expense.amountPaise)}
                  </p>
                  {expense.systemGenerated ? (
                    <span className="text-xs text-muted-foreground">
                      {t('expenses.systemLocked')}
                    </span>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setDialog({ expense })}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void del(expense)}
                      >
                        {t('common.delete')}
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {dialog ? (
        <RecordExpenseDialog
          categories={categoryList}
          expense={dialog.expense}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

// --- Standing instructions, and what they are waiting on ------------------------------------

function RepeatingPanel(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();

  const pending = usePendingExpenses();
  const recurring = useRecurringExpenses();
  const categories = useExpenseCategories();
  const confirmOne = useConfirmPendingExpense();
  const dismissOne = useDismissPendingExpense();
  const removeInstruction = useDeleteRecurringExpense();

  const [creating, setCreating] = useState(false);

  const items = pending.data?.items ?? [];

  const accept = async (expense: Expense): Promise<void> => {
    try {
      await confirmOne.mutateAsync({ expenseId: expense.id });
      toast({
        title: t('expenses.confirmed', { amount: formatCurrency(expense.amountPaise) }),
        variant: 'success',
      });
    } catch (caught) {
      toast({
        title: caught instanceof Error ? caught.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  const skip = async (expense: Expense): Promise<void> => {
    const ok = await confirm({
      title: t('expenses.dismissTitle'),
      description: t('expenses.dismissHint'),
      destructive: true,
    });
    if (!ok) return;
    await dismissOne.mutateAsync(expense.id);
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">{t('expenses.awaitingHeading')}</h2>
        <p className="text-sm text-muted-foreground">{t('expenses.awaitingHint')}</p>

        {pending.isPending ? (
          <ListSkeleton rows={2} />
        ) : pending.isError ? (
          <LoadError onRetry={() => void pending.refetch()} />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('expenses.awaitingNone')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((expense) => (
              <li
                key={expense.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning/5 p-3"
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {expense.vendor ?? expense.categoryLabel}
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      {expense.categoryLabel}
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('expenses.dueOn', { date: formatDate(expense.incurredOn) })}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-semibold tabular-nums">
                    {formatCurrency(expense.amountPaise)}
                  </p>
                  <Button type="button" size="sm" onClick={() => void accept(expense)}>
                    {t('expenses.confirm')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void skip(expense)}
                  >
                    {t('common.dismiss')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">{t('expenses.recurringHeading')}</h2>
          <Button type="button" variant="outline" size="sm" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t('expenses.addRecurring')}
          </Button>
        </div>

        {recurring.isPending ? (
          <ListSkeleton rows={2} />
        ) : recurring.isError ? (
          <LoadError onRetry={() => void recurring.refetch()} />
        ) : (recurring.data?.items ?? []).length === 0 ? (
          <EmptyState
            icon={CalendarClock}
            title={t('expenses.recurringEmptyTitle')}
            description={t('expenses.recurringEmptyHint')}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {(recurring.data?.items ?? []).map((one) => (
              <li
                key={one.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {one.label}
                    {one.active ? null : <Badge variant="outline">{t('expenses.paused')}</Badge>}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {one.categoryLabel} ·{' '}
                    {one.frequency === 'monthly'
                      ? t('expenses.everyMonthOn', { day: one.dayOfMonth ?? 1 })
                      : t('expenses.everyWeek')}{' '}
                    · {t('expenses.nextDue', { date: formatDate(one.nextRunOn) })}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-semibold tabular-nums">{formatCurrency(one.amountPaise)}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void removeInstruction.mutateAsync(one.id)}
                  >
                    {t('expenses.stop')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {creating ? (
        <NewRecurringForm
          categories={categories.data?.items ?? []}
          onClose={() => setCreating(false)}
        />
      ) : null}
    </div>
  );
}

function NewRecurringForm({
  categories,
  onClose,
}: {
  categories: readonly ExpenseCategory[];
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const create = useCreateRecurringExpense();

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [label, setLabel] = useState('');
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [frequency, setFrequency] = useState<RecurrenceFrequency>('monthly');
  const [dayOfMonth, setDayOfMonth] = useState('5');
  const [weekday, setWeekday] = useState('1');
  const [startsOn, setStartsOn] = useState(today);
  const [error, setError] = useState<string | null>(null);

  const amountPaise = ((): number => {
    try {
      return amount.trim() === '' ? 0 : parseRupees(amount);
    } catch {
      return 0;
    }
  })();

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      await create.mutateAsync({
        label: label.trim(),
        categoryId,
        amountPaise,
        method,
        frequency,
        startsOn,
        ...(frequency === 'monthly'
          ? { dayOfMonth: Number(dayOfMonth) }
          : { weekday: Number(weekday) }),
      });
      toast({ title: t('expenses.recurringAdded'), variant: 'success' });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <h3 className="text-sm font-semibold">{t('expenses.addRecurring')}</h3>
      {error ? (
        <p role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="recurring-label">{t('expenses.recurringLabel')}</Label>
          <Input
            id="recurring-label"
            value={label}
            placeholder={t('expenses.recurringLabelPlaceholder')}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="recurring-amount">{t('expenses.amount')}</Label>
          <Input
            id="recurring-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="recurring-category">{t('expenses.category')}</Label>
          <select
            id="recurring-category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
          >
            {categories.map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="recurring-method">{t('expenses.method')}</Label>
          <select
            id="recurring-method"
            value={method}
            onChange={(event) => setMethod(event.target.value as PaymentMethod)}
            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
          >
            {PAYMENT_METHODS.map((one) => (
              <option key={one} value={one}>
                {t(`payments.methods.${one}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="recurring-frequency">{t('expenses.frequency')}</Label>
          <select
            id="recurring-frequency"
            value={frequency}
            onChange={(event) => setFrequency(event.target.value as RecurrenceFrequency)}
            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
          >
            <option value="monthly">{t('expenses.monthly')}</option>
            <option value="weekly">{t('expenses.weekly')}</option>
          </select>
        </div>
        {frequency === 'monthly' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="recurring-day">{t('expenses.dayOfMonth')}</Label>
            <Input
              id="recurring-day"
              type="number"
              min={1}
              max={31}
              value={dayOfMonth}
              onChange={(event) => setDayOfMonth(event.target.value)}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="recurring-weekday">{t('expenses.weekday')}</Label>
            <select
              id="recurring-weekday"
              value={weekday}
              onChange={(event) => setWeekday(event.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {[0, 1, 2, 3, 4, 5, 6].map((one) => (
                <option key={one} value={one}>
                  {t(`expenses.weekdays.${one}`)}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="recurring-starts">{t('expenses.startsOn')}</Label>
          <Input
            id="recurring-starts"
            type="date"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          disabled={amountPaise <= 0 || label.trim() === '' || create.isPending}
          onClick={() => void submit()}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

// --- The shop's own list -------------------------------------------------------------------

function CategoriesPanel(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const categories = useExpenseCategories();
  const create = useCreateExpenseCategory();
  const update = useUpdateExpenseCategory();

  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = async (): Promise<void> => {
    setError(null);
    try {
      await create.mutateAsync({ label: label.trim() });
      setLabel('');
      toast({ title: t('expenses.categoryAdded'), variant: 'success' });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  const items = categories.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">{t('expenses.categoriesHint')}</p>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="category-label">{t('expenses.newCategory')}</Label>
          <Input
            id="category-label"
            value={label}
            placeholder={t('expenses.newCategoryPlaceholder')}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>
        <Button
          type="button"
          disabled={label.trim() === '' || create.isPending}
          onClick={() => void add()}
        >
          <Plus aria-hidden="true" />
          {t('expenses.addCategory')}
        </Button>
      </div>

      {error ? (
        <p role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      ) : null}

      {categories.isPending ? (
        <ListSkeleton rows={4} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tag}
          title={t('expenses.categoriesEmptyTitle')}
          description={t('expenses.categoriesHint')}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((category) => (
            <li
              key={category.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
            >
              <span className="flex items-center gap-2">
                {category.label}
                {category.seeded ? (
                  <Badge variant="outline">{t('expenses.seededBadge')}</Badge>
                ) : null}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  void update.mutateAsync({
                    categoryId: category.id,
                    input: { archived: true },
                  })
                }
              >
                {t('expenses.archive')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ExpensesPage;
